/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\CustomTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Templates module.
----------------------------------------------------------------*/

import { Check, Code, Copy, Download, FileCode, Loader2, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import Editor from '@monaco-editor/react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import type * as api from '../api';

interface CustomTabProps {
  selectedModel: api.DeviceModel | null;
  control: Control<api.Template>;
  isLoadingTemplate: boolean;
  originalTemplateContent: string;
  getValues: (field: 'customConfig') => string;
  setValue: (field: 'customConfig', value: string) => void;
}

export function CustomTab({
  selectedModel,
  control,
  isLoadingTemplate,
  originalTemplateContent,
  getValues,
  setValue,
}: CustomTabProps) {
  const { t } = useI18n();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [editorInstance, setEditorInstance] = useState<any>(null);

  const handleEditorDidMount = (editor: unknown, _monaco: unknown) => {
    setEditorInstance(editor);
  };

  const handleCopy = () => {
    const content = getValues('customConfig') || '';
    navigator.clipboard.writeText(content);
    setCopiedToClipboard(true);
    setTimeout(() => setCopiedToClipboard(false), 2000);
  };

  const handleDownload = () => {
    const content = getValues('customConfig') || '';
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `template-${selectedModel?.model || 'custom'}.cfg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const headerContent = (
    <div className="flex items-center gap-1">
      {originalTemplateContent && (
        <SimpleTooltip content={t('provisioningTemplates.resetToOriginal', 'Reset to original template')}>
          <button type="button" onClick={() => setValue('customConfig', originalTemplateContent)}
            className="p-1.5 text-orange-600 dark:text-orange-400 hover:bg-orange-500/20 rounded-md transition-colors">
            <RefreshCw className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      )}
      <SimpleTooltip content={copiedToClipboard ? t('common.copied', 'Copied!') : t('common.copy', 'Copy to clipboard')}>
        <button type="button" onClick={handleCopy}
          className="p-1.5 text-muted-foreground hover:bg-muted rounded-md transition-colors">
          {copiedToClipboard ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.download', 'Download template')}>
        <button type="button" onClick={handleDownload}
          className="p-1.5 text-primary hover:bg-primary/20 rounded-md transition-colors">
          <Download className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );

  return (
    <FormBox title={t('provisioningTemplates.tabs.custom', 'Custom Configuration')} icon={<Code className="h-4 w-4" />} headerContent={headerContent}>
      {isLoadingTemplate ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">{t('provisioningTemplates.loadingTemplate', 'Loading template...')}</span>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="h-[600px] border border-border rounded-lg overflow-hidden relative shadow-sm">
            <Controller name="customConfig" control={control}
              render={({ field }) => (
                <Editor height="100%" defaultLanguage="handlebars"
                  theme={document.documentElement.classList.contains('dark') ? 'vs-dark' : 'light'}
                  value={field.value || ''} onChange={(value) => field.onChange(value)}
                  onMount={handleEditorDidMount}
                  options={{
                    minimap: { enabled: true }, fontSize: 13, scrollBeyondLastLine: false, wordWrap: 'on',
                    automaticLayout: true, padding: { top: 16, bottom: 16 },
                    fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, 'Courier New', monospace",
                    fontLigatures: true, renderWhitespace: 'selection',
                  }}
                  loading={<div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}
                />
              )} />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
            <span>{t('provisioningTemplates.editorHint', 'Press F1 for command palette. Support for Nunjucks/Jinja2 syntax highlighting.')}</span>
          </div>
          {selectedModel && (
            <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-2">
                <FileCode className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{t('provisioningTemplates.availableVariables', 'Available Variables')}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {['mac', 'extension', 'password', 'displayName', 'sipServer', 'sipPort', 'timezone', 'language', 'adminPassword'].map((variable) => (
                  <code key={variable}
                    className="px-2 py-1 text-xs bg-primary/10 text-primary rounded border border-primary/20 cursor-pointer hover:bg-primary/20 transition-colors"
                    onClick={() => {
                      const variableTag = `{{ ${variable} }}`;
                      if (editorInstance) {
                        const selection = editorInstance.getSelection();
                        editorInstance.executeEdits('my-source', [{ range: selection, text: variableTag, forceMoveMarkers: true }]);
                        editorInstance.pushUndoStop();
                      } else {
                        const currentValue = getValues('customConfig') || '';
                        setValue('customConfig', currentValue + variableTag);
                      }
                    }}
                    title={t('provisioningTemplates.clickToInsert', 'Click to insert')}>
                    {'{{ ' + variable + ' }}'}
                  </code>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </FormBox>
  );
}
